import { useEffect, useMemo, useRef, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Button, Input } from '@/components/ui';
import { useTelemetry } from '@/hooks/useTelemetry';
import { useAttributes } from '@/hooks/useAttributes';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { tbApi } from '@/lib/tbApi';
import { api } from '@/lib/api';
import { ExportButton } from '@/components/greenhouse/ExportButton';
import { cn, formatNumber, normalizeBoolean } from '@/lib/utils';
import {
  Beaker, FlaskConical, Droplets, Thermometer, Sun, Wind, ShieldAlert,
  AlertTriangle, RefreshCw, Wifi, WifiOff, Clock, RotateCcw, Power, StopCircle, Minus, Plus, LineChart as LineChartIcon,
  Gauge, Sliders, Settings2, Sparkles, Hand, PauseCircle, Target, ShieldCheck, Activity, Zap, DownloadCloud,
} from 'lucide-react';

function SectionTitle({ icon: Icon, iconBg, iconColor, title, action }: {
  icon: typeof Gauge; iconBg: string; iconColor: string; title: string; action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5 mb-4">
      <div className="w-8 h-8 rounded-xl backdrop-blur-sm flex items-center justify-center shrink-0" style={{ background: iconBg, color: iconColor }}>
        <Icon className="w-4 h-4" />
      </div>
      <span className="text-sm font-bold text-gray-900 dark:text-gray-100 tracking-tight">{title}</span>
      <div className="flex-1 h-px bg-white/60 dark:bg-white/10" />
      {action}
    </div>
  );
}

// Glass (frosted) card — floats translucent over the page's background blobs
function GlassCard({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn(
      'rounded-3xl border border-white/60 dark:border-white/10',
      'bg-white/55 dark:bg-white/[0.06] backdrop-blur-xl',
      'shadow-[0_8px_32px_-8px_rgba(31,38,135,0.15)] dark:shadow-[0_8px_32px_-8px_rgba(0,0,0,0.4)]',
      className
    )}>
      {children}
    </div>
  );
}

// Soft blurred color blobs behind the whole page — the colorful backdrop glass
// surfaces need to actually read as "frosted glass" rather than plain white.
// Deliberately NOT using a negative z-index: a positioned ancestor with no
// z-index of its own (our outer `relative` wrapper) still forms an effective
// stacking layer above negative-z-index descendants at the root context, so
// `-z-10` silently rendered this entirely behind the page background and was
// invisible (confirmed by isolating this exact markup in a standalone test
// page). Instead this relies purely on DOM order - rendered first, so later
// siblings (the cards) naturally paint over it wherever they overlap.
// `inset-0` (not a fixed height) so it always matches whichever tab's actual
// content height is currently rendered - a fixed height taller than a short
// tab (e.g. one settings group) left a big empty gradient strip below the
// real content, which read as visually unbalanced.
function BackgroundAurora() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute -top-32 -left-20 w-[26rem] h-[26rem] rounded-full bg-indigo-400/40 dark:bg-indigo-500/20 blur-3xl" />
      <div className="absolute top-1/3 -right-24 w-[26rem] h-[26rem] rounded-full bg-emerald-400/35 dark:bg-emerald-500/15 blur-3xl" />
      <div className="absolute bottom-0 left-1/4 w-[26rem] h-[26rem] rounded-full bg-amber-300/30 dark:bg-amber-500/15 blur-3xl" />
    </div>
  );
}

type AutoHydroTab = 'overview' | 'chart' | 'control' | 'settings';
const PAGE_TABS: { id: AutoHydroTab; label: string; icon: typeof Gauge }[] = [
  { id: 'overview', label: 'ภาพรวม', icon: Gauge },
  { id: 'chart', label: 'กราฟ', icon: LineChartIcon },
  { id: 'control', label: 'ควบคุม', icon: Sliders },
  { id: 'settings', label: 'ตั้งค่า', icon: Settings2 },
];

interface AutoHydroGreenhousePageProps {
  project: string;
  gh: string;
  greenhouseId: number;
  greenhouseName: string;
  hasDevice: boolean;
}

// ปูมย้อนหลังใช้ตาราง hydro_sensor_readings ตัวเดิม - hydroSync.ts sync ให้อัตโนมัติ
// อยู่แล้วเพราะโปรเจกต์นี้ตั้ง project_type='hydroponics' เหมือนโปรเจกต์ Hydro เดิม
// (บอร์ดนี้ไม่มีเซนเซอร์ CO2 จึงไม่รวม co2 ในตัวเลือก และยังไม่ sync tds จนกว่าจะต่อ RS485 จริง)
interface HydroReading {
  recorded_at: string;
  ec: number | null;
  ph: number | null;
  water_temp: number | null;
  air_temp: number | null;
  humidity: number | null;
  lux: number | null;
}

const CHART_METRICS: { key: keyof HydroReading; label: string; unit: string; colorRgb: string; decimals: number }[] = [
  { key: 'ec', label: 'EC', unit: 'µS/cm', colorRgb: '216,90,48', decimals: 0 },
  { key: 'ph', label: 'pH', unit: '', colorRgb: '24,95,165', decimals: 2 },
  { key: 'water_temp', label: 'อุณหภูมิน้ำ', unit: '°C', colorRgb: '15,110,86', decimals: 1 },
  { key: 'air_temp', label: 'อุณหภูมิอากาศ', unit: '°C', colorRgb: '153,60,29', decimals: 1 },
  { key: 'humidity', label: 'ความชื้นอากาศ', unit: '%', colorRgb: '15,110,86', decimals: 1 },
  { key: 'lux', label: 'ความเข้มแสง', unit: ' lux', colorRgb: '133,79,11', decimals: 0 },
];
const CHART_RANGES: { key: '24h' | '7d' | '30d'; label: string; hours: number }[] = [
  { key: '24h', label: '24 ชม.', hours: 24 },
  { key: '7d', label: '7 วัน', hours: 24 * 7 },
  { key: '30d', label: '30 วัน', hours: 24 * 30 },
];

function downsample<T>(arr: T[], maxPoints: number): T[] {
  if (arr.length <= maxPoints) return arr;
  const step = arr.length / maxPoints;
  const out: T[] = [];
  for (let i = 0; i < maxPoints; i++) out.push(arr[Math.floor(i * step)]);
  return out;
}

function HistoryChartSection({ project, gh, greenhouseId }: { project: string; gh: string; greenhouseId: number }) {
  const [range, setRange] = useState<'24h' | '7d' | '30d'>('24h');
  const [metricKey, setMetricKey] = useState<keyof HydroReading>('ec');
  const [rows, setRows] = useState<HydroReading[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const rangeCfg = CHART_RANGES.find((r) => r.key === range)!;
    setIsLoading(true);
    api.get<{ readings: HydroReading[] }>(`/hydro/readings/history/${greenhouseId}?hours=${rangeCfg.hours}`)
      .then((res) => {
        if (res.success && res.data?.readings) setRows(downsample(res.data.readings, 200));
      })
      .catch((e) => console.error(e))
      .finally(() => setIsLoading(false));
  }, [greenhouseId, range]);

  const metric = CHART_METRICS.find((m) => m.key === metricKey)!;
  const chartData = useMemo(() => rows
    .map((r) => ({ ts: r.recorded_at, value: r[metric.key] as number | null }))
    .filter((d) => d.value !== null), [rows, metric.key]);

  const stats = useMemo(() => {
    const values = chartData.map((d) => d.value as number);
    if (values.length === 0) return null;
    return {
      latest: values[values.length - 1],
      min: Math.min(...values),
      max: Math.max(...values),
      avg: values.reduce((a, b) => a + b, 0) / values.length,
    };
  }, [chartData]);

  const dateFmt = (ts: string) => {
    const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
    return range === '24h'
      ? d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit' });
  };
  const fullDateFmt = (ts: string) => {
    const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
    return d.toLocaleString('th-TH', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <GlassCard className="p-5">
      <SectionTitle
        icon={LineChartIcon} iconBg="#F3E8FF" iconColor="#7C3AED" title="กราฟย้อนหลัง"
        action={<ExportButton projectKey={project} ghKey={gh} telemetryKeys={DOSING_TELEMETRY_KEYS} />}
      />

      {stats && (
        <div className="grid grid-cols-4 gap-2.5 mb-4">
          {[
            { label: 'ล่าสุด', value: stats.latest, emphasis: true },
            { label: 'ต่ำสุด', value: stats.min, emphasis: false },
            { label: 'เฉลี่ย', value: stats.avg, emphasis: false },
            { label: 'สูงสุด', value: stats.max, emphasis: false },
          ].map((s) => (
            <div
              key={s.label}
              className="rounded-xl border border-white/50 dark:border-white/10 bg-white/25 dark:bg-white/[0.03] backdrop-blur-md px-3 py-2.5 text-center"
              style={s.emphasis ? { background: `rgba(${metric.colorRgb},0.10)`, borderColor: `rgba(${metric.colorRgb},0.3)` } : undefined}
            >
              <div className="text-[10px] text-gray-500 dark:text-gray-400">{s.label}</div>
              <div
                className={cn('text-sm font-bold tabular-nums', !s.emphasis && 'text-gray-700 dark:text-gray-300')}
                style={s.emphasis ? { color: `rgb(${metric.colorRgb})` } : undefined}
              >
                {s.value.toFixed(metric.decimals)}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-1.5">
          {CHART_METRICS.map((m) => (
            <button
              key={m.key}
              onClick={() => setMetricKey(m.key)}
              className={cn(
                'px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors',
                metricKey === m.key
                  ? 'text-white border-transparent'
                  : 'text-gray-500 dark:text-gray-400 border-white/50 dark:border-white/10 bg-white/20 dark:bg-white/[0.03] hover:bg-white/50 dark:hover:bg-white/10'
              )}
              style={metricKey === m.key ? { background: `rgb(${m.colorRgb})` } : undefined}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 rounded-full bg-white/30 dark:bg-white/5 backdrop-blur-sm p-0.5">
          {CHART_RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={cn(
                'px-3 py-1 rounded-full text-[11px] font-semibold transition-colors',
                range === r.key
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400'
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="h-[220px] flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">กำลังโหลด...</div>
      ) : chartData.length < 2 ? (
        <div className="h-[220px] flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">ยังไม่มีข้อมูลเพียงพอสำหรับช่วงเวลานี้</div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-gray-100 dark:stroke-gray-800" />
            <XAxis dataKey="ts" tickFormatter={dateFmt} tick={{ fontSize: 11 }} minTickGap={30} />
            <YAxis tick={{ fontSize: 11 }} width={44} domain={['auto', 'auto']} />
            <Tooltip
              labelFormatter={(ts) => fullDateFmt(ts as string)}
              formatter={(value: number) => [`${value.toFixed(metric.decimals)}${metric.unit}`, metric.label]}
            />
            <Line type="monotone" dataKey="value" stroke={`rgb(${metric.colorRgb})`} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </GlassCard>
  );
}

// telemetry ทั้งหมดจากบอร์ด dosing controller (ดู WEBAPP_INTEGRATION.md ของ
// Automatic_Hydroponics/Automatics_test ฝั่งเฟิร์มแวร์ - ยึด src/ThingsBoardClient.cpp
// เป็นความจริงสูงสุดถ้าเอกสารกับโค้ดไม่ตรงกัน)
const DOSING_TELEMETRY_KEYS = [
  'ec', 'ph', 'water_temp', 'air_temp', 'air_humidity', 'air_light', 'tds', 'salinity',
  'pump_a_state', 'pump_b_state', 'pump_ph_down_state',
  'mode', 'dosing_state', 'safety_tripped', 'ph_below_min_alert',
  'total_dose_ml_today_a', 'total_dose_ml_today_b', 'total_dose_ml_today_ph',
  'wifi_rssi', 'uptime', 'firmware_version', 'ota_status',
];

// status/last_seen เป็น client attribute (ไม่ใช่ telemetry) - รวมไว้ในชุดเดียวกับ
// shared attribute setpoint เพราะ endpoint /values/attributes ของ ThingsBoard คืนทุก scope รวมกัน
const DOSING_ATTR_KEYS = [
  'ec_target_min', 'ec_target_max', 'ph_target_min', 'ph_target_max',
  'dose_pulse_seconds', 'ph_dose_pulse_seconds', 'ec_mixing_wait_seconds', 'ph_mixing_wait_seconds',
  'idle_cooldown_seconds', 'reservoir_volume_liters',
  'flow_rate_a', 'flow_rate_b', 'flow_rate_ph',
  'max_dose_ml_per_day_a', 'max_dose_ml_per_day_b', 'max_dose_ml_per_day_ph',
  'status', 'last_seen',
];

interface NumField {
  key: string;
  label: string;
  unit: string;
  step: number;
  min?: number;
  max?: number;
}

const EC_PH_FIELDS: NumField[] = [
  { key: 'ec_target_min', label: 'EC ต่ำสุด (เริ่มโดสปุ๋ย)', unit: 'µS/cm', step: 50, min: 0, max: 20000 },
  { key: 'ec_target_max', label: 'EC สูงสุด (หยุดโดส)', unit: 'µS/cm', step: 50, min: 0, max: 20000 },
  { key: 'ph_target_min', label: 'pH ต่ำสุด (แจ้งเตือน แก้เองไม่ได้)', unit: '', step: 0.1, min: 0, max: 14 },
  { key: 'ph_target_max', label: 'pH สูงสุด (เริ่มโดส pH Down)', unit: '', step: 0.1, min: 0, max: 14 },
];

const TIMING_FIELDS: NumField[] = [
  { key: 'dose_pulse_seconds', label: 'ระยะเปิดปั๊ม A+B ต่อครั้ง', unit: 'วินาที', step: 1, min: 1, max: 30 },
  { key: 'ph_dose_pulse_seconds', label: 'ระยะเปิดปั๊ม pH Down ต่อครั้ง', unit: 'วินาที', step: 1, min: 1, max: 30 },
  { key: 'ec_mixing_wait_seconds', label: 'รอผสมหลังโดส EC', unit: 'วินาที', step: 5, min: 0 },
  { key: 'ph_mixing_wait_seconds', label: 'รอผสมหลังโดส pH', unit: 'วินาที', step: 5, min: 0 },
  { key: 'idle_cooldown_seconds', label: 'พักก่อนเริ่มเช็ครอบใหม่', unit: 'วินาที', step: 30, min: 0 },
];

const SAFETY_FIELDS: NumField[] = [
  { key: 'reservoir_volume_liters', label: 'ขนาดถังจริง', unit: 'ลิตร', step: 1, min: 1 },
  { key: 'max_dose_ml_per_day_a', label: 'เพดานโดส Nutrient A/วัน', unit: 'ml', step: 100, min: 0 },
  { key: 'max_dose_ml_per_day_b', label: 'เพดานโดส Nutrient B/วัน', unit: 'ml', step: 100, min: 0 },
  { key: 'max_dose_ml_per_day_ph', label: 'เพดานโดส pH Down/วัน', unit: 'ml', step: 100, min: 0 },
];

const ALL_EDITABLE_FIELDS = [...EC_PH_FIELDS, ...TIMING_FIELDS, ...SAFETY_FIELDS];

const SETTINGS_GROUPS: { id: 'ecph' | 'timing' | 'safety' | 'flow'; label: string; icon: typeof Gauge; iconBg: string; iconColor: string }[] = [
  { id: 'ecph', label: 'เป้าหมาย EC / pH', icon: Target, iconBg: '#F5F3FF', iconColor: '#6D28D9' },
  { id: 'timing', label: 'จังหวะเวลาโดส', icon: Clock, iconBg: '#EFF6FF', iconColor: '#1D4ED8' },
  { id: 'safety', label: 'ขีดจำกัดความปลอดภัย', icon: ShieldCheck, iconBg: '#FEF2F2', iconColor: '#B91C1C' },
  { id: 'flow', label: 'อัตราการไหลปั๊ม', icon: Gauge, iconBg: '#F3F4F6', iconColor: '#4B5563' },
];

const FLOW_RATE_FIELDS: { key: string; label: string }[] = [
  { key: 'flow_rate_a', label: 'Nutrient A' },
  { key: 'flow_rate_b', label: 'Nutrient B' },
  { key: 'flow_rate_ph', label: 'pH Down' },
];

const PUMPS = [
  { id: 0, name: 'Nutrient A', dot: 'bg-emerald-500', colorRgb: '16,185,129' },
  { id: 1, name: 'Nutrient B', dot: 'bg-sky-500', colorRgb: '14,165,233' },
  { id: 2, name: 'pH Down', dot: 'bg-amber-500', colorRgb: '217,119,6' },
] as const;

const MODE_META: Record<'AUTO' | 'MANUAL' | 'PAUSED', { icon: typeof Sparkles; caption: string; colorRgb: string }> = {
  AUTO: { icon: Sparkles, caption: 'ทำงานอัตโนมัติเต็มรูปแบบตามค่าตั้งต้น', colorRgb: '16,185,129' },
  MANUAL: { icon: Hand, caption: 'สั่งเปิด/ปิดปั๊มเองได้ทีละตัว', colorRgb: '217,119,6' },
  PAUSED: { icon: PauseCircle, caption: 'หยุดโดสทั้งหมดชั่วคราว', colorRgb: '107,114,128' },
};

const MODE_STYLE: Record<string, string> = {
  AUTO: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-700',
  MANUAL: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-700',
  PAUSED: 'bg-gray-200 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600',
};

const DOSING_STATE_LABELS: Record<string, string> = {
  IDLE: 'ว่าง',
  PH_DOSING: 'กำลังโดส pH Down',
  PH_MIXING_WAIT: 'รอผสม (หลังโดส pH)',
  EC_DOSING: 'กำลังโดสปุ๋ย A+B',
  EC_MIXING_WAIT: 'รอผสม (หลังโดส EC)',
  IDLE_COOLDOWN: 'พักรอบ (Cooldown)',
  SAFETY_TRIPPED: 'หยุดฉุกเฉิน',
};

function formatUptime(seconds: number | null): string {
  if (seconds === null || seconds < 0 || Number.isNaN(seconds)) return '--';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d} วัน ${h} ชม.`;
  if (h > 0) return `${h} ชม. ${m} นาที`;
  return `${m} นาที`;
}

// Flat stat card - copied from AirSensorCard.tsx's already-proven pattern
// (used on the Tomato project's ค่าดิน tab) instead of inventing new styling:
// icon in a colored rounded square, label above a big bold number, red/amber
// highlight border when out of range.
function SensorStat({ icon: Icon, label, value, unit, colorRgb, decimals = 1, warnMin, warnMax }: {
  icon: typeof Droplets; label: string; value: number | null; unit: string; colorRgb: string;
  decimals?: number; warnMin?: number; warnMax?: number;
}) {
  const hasValue = value !== null;
  const hasWarnRange = warnMin !== undefined && warnMax !== undefined;
  const isWarn = hasWarnRange && hasValue && (value < warnMin! || value > warnMax!);
  const displayValue = hasValue ? formatNumber(value, decimals) : '--';

  return (
    <div className={cn(
      'flex items-center gap-3 p-4 rounded-xl transition-all',
      isWarn
        ? 'bg-amber-50 border-2 border-amber-300 dark:bg-amber-950 dark:border-amber-700'
        : 'bg-white/70 dark:bg-white/5 border border-white/60 dark:border-white/10 backdrop-blur-md'
    )}>
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: hasValue ? `rgba(${colorRgb},0.15)` : 'rgba(148,163,184,0.15)' }}
      >
        <Icon className="w-6 h-6" style={{ color: hasValue ? `rgb(${colorRgb})` : '#94a3b8' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</p>
        <div className="flex items-baseline gap-1 flex-wrap">
          <span className="text-xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">{displayValue}</span>
          {hasValue && unit && <span className="text-xs font-normal text-gray-500 dark:text-gray-400">{unit}</span>}
        </div>
        {!hasValue && <span className="text-[11px] text-gray-400 dark:text-gray-500">ยังไม่ต่อเซนเซอร์</span>}
        {isWarn && (
          <div className="flex items-center gap-1 mt-0.5">
            <AlertTriangle className="w-3 h-3 text-amber-600" />
            <span className="text-[11px] text-amber-600 font-medium">นอกเกณฑ์</span>
          </div>
        )}
      </div>
    </div>
  );
}

function DoseBar({ label, value, max, colorClass }: { label: string; value: number | null; max: number | null; colorClass: string }) {
  const v = value ?? 0;
  const m = max && max > 0 ? max : null;
  const pct = m ? Math.min(100, (v / m) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1 text-xs">
        <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-gray-500 dark:text-gray-400 tabular-nums">
          {formatNumber(v, 0)} {m ? `/ ${formatNumber(m, 0)}` : ''} ml
        </span>
      </div>
      <div className="h-2 rounded-full bg-white/40 dark:bg-white/5 overflow-hidden">
        <div className={cn('h-full rounded-full transition-[width] duration-500', colorClass)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function AutoHydroGreenhousePage({ project, gh, greenhouseId, greenhouseName, hasDevice }: AutoHydroGreenhousePageProps) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const canControl = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'operator';

  const telemetry = useTelemetry({ project, gh, keys: DOSING_TELEMETRY_KEYS, enabled: hasDevice, pollInterval: 10000 });
  const attrs = useAttributes({ project, gh, keys: DOSING_ATTR_KEYS, enabled: hasDevice, pollInterval: 0 });

  const t = telemetry.data;
  const ec = (t.ec as number | null) ?? null;
  const ph = (t.ph as number | null) ?? null;
  const waterTemp = (t.water_temp as number | null) ?? null;
  const airTemp = (t.air_temp as number | null) ?? null;
  const airHumidity = (t.air_humidity as number | null) ?? null;
  const airLight = (t.air_light as number | null) ?? null;
  const mode = (t.mode as string | null) ?? null;
  const dosingState = (t.dosing_state as string | null) ?? null;
  const safetyTripped = normalizeBoolean(t.safety_tripped);
  const phBelowMinAlert = normalizeBoolean(t.ph_below_min_alert);
  const wifiRssi = (t.wifi_rssi as number | null) ?? null;
  const uptimeSec = (t.uptime as number | null) ?? null;
  const fwVersion = (t.firmware_version as string | null) ?? null;
  const otaStatus = (t.ota_status as string | null) ?? null;
  const pumpOn: Record<number, boolean> = {
    0: normalizeBoolean(t.pump_a_state),
    1: normalizeBoolean(t.pump_b_state),
    2: normalizeBoolean(t.pump_ph_down_state),
  };

  // ---- ตั้งค่า (shared attributes) ----
  const [draft, setDraft] = useState<Record<string, string>>({});
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || attrs.isLoading) return;
    const seed: Record<string, string> = {};
    for (const f of ALL_EDITABLE_FIELDS) {
      const v = attrs.data[f.key];
      if (v !== undefined && v !== null) seed[f.key] = String(v);
    }
    setDraft(seed);
    seededRef.current = true;
  }, [attrs.isLoading, attrs.data]);

  const [savingSettings, setSavingSettings] = useState(false);
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload: Record<string, number> = {};
      for (const f of ALL_EDITABLE_FIELDS) {
        const raw = draft[f.key];
        if (raw === undefined || raw === '') continue;
        const n = Number(raw);
        if (Number.isNaN(n)) continue;
        payload[f.key] = n;
      }
      await tbApi.setAttributes(project, gh, payload);
      addToast('success', 'บันทึกการตั้งค่าแล้ว');
      seededRef.current = false; // re-seed จากค่าที่เพิ่ง save
      await attrs.refetch();
    } catch (e) {
      addToast('error', e instanceof Error ? e.message : 'บันทึกการตั้งค่าไม่สำเร็จ');
    } finally {
      setSavingSettings(false);
    }
  };

  // ---- โหมด ----
  const [modeSending, setModeSending] = useState(false);
  const handleSetMode = async (newMode: 'AUTO' | 'MANUAL' | 'PAUSED') => {
    if (newMode === mode) return;
    setModeSending(true);
    try {
      const res = await tbApi.sendRpc(project, gh, 'setMode', newMode, 5000);
      if (res.rpcResponse?.result === 'ok') {
        addToast('success', `เปลี่ยนโหมดเป็น ${newMode} แล้ว`);
      } else {
        addToast('warning', 'ส่งคำสั่งแล้ว รอซิงค์สถานะจากบอร์ด...');
      }
    } catch (e) {
      addToast('error', e instanceof Error ? e.message : 'สั่งเปลี่ยนโหมดไม่สำเร็จ');
    } finally {
      setModeSending(false);
      telemetry.refetch();
    }
  };

  // ---- ปั๊มแบบแมนนวล ----
  const [pumpDuration, setPumpDuration] = useState<Record<number, string>>({ 0: '4', 1: '4', 2: '3' });
  const [pumpSending, setPumpSending] = useState<Record<number, boolean>>({});
  // Local "is running" state per pump, driven entirely by our own timer once
  // WE trigger a pulse - undefined means "no local action yet, defer to
  // telemetry" (e.g. on first load). The board's telemetry publish cycle is
  // much slower (up to ~10s) than a dose pulse can be (as short as 1s), and
  // only runs on its own fixed schedule rather than being triggered by state
  // changes - so a plain `optimistic || telemetry` OR let stale telemetry
  // (still "on" from before the board has published its own "off" yet, or
  // vice versa) override the correct local state in either direction: the
  // button either never lit up, or kept rippling after the pulse had
  // genuinely ended. Once we've triggered a pulse locally we trust our own
  // clock for its entire on+off lifecycle instead of re-consulting telemetry.
  const [pumpLocalOn, setPumpLocalOn] = useState<Record<number, boolean | undefined>>({});
  const [pumpCountdown, setPumpCountdown] = useState<Record<number, number>>({});
  const pumpTimersRef = useRef<Record<number, { off?: ReturnType<typeof setTimeout>; tick?: ReturnType<typeof setInterval> }>>({});

  const clearPumpTimers = (pumpId: number) => {
    const timers = pumpTimersRef.current[pumpId];
    if (timers) {
      clearTimeout(timers.off);
      clearInterval(timers.tick);
    }
    pumpTimersRef.current[pumpId] = {};
  };
  useEffect(() => () => {
    Object.keys(pumpTimersRef.current).forEach((id) => clearPumpTimers(Number(id)));
  }, []);

  const handlePump = async (pumpId: number, on: boolean) => {
    const raw = Number(pumpDuration[pumpId] ?? 4);
    const duration = on ? Math.min(Math.max(Math.round(raw) || 1, 1), 30) : 0;
    setPumpSending((p) => ({ ...p, [pumpId]: true }));
    try {
      const res = await tbApi.sendRpc(project, gh, 'manualPumpControl', { pump_id: pumpId, on, duration }, 5000);
      if (res.rpcResponse?.result === 'rejected') {
        addToast('warning', 'บอร์ดปฏิเสธคำสั่ง (ต้องอยู่ในโหมด MANUAL เท่านั้น)');
      } else {
        clearPumpTimers(pumpId);
        if (on) {
          setPumpLocalOn((p) => ({ ...p, [pumpId]: true }));
          setPumpCountdown((p) => ({ ...p, [pumpId]: duration }));
          const tick = setInterval(() => {
            setPumpCountdown((p) => ({ ...p, [pumpId]: Math.max(0, (p[pumpId] ?? 1) - 1) }));
          }, 1000);
          const off = setTimeout(() => {
            setPumpLocalOn((p) => ({ ...p, [pumpId]: false }));
            clearInterval(pumpTimersRef.current[pumpId]?.tick);
            telemetry.refetch();
          }, duration * 1000);
          pumpTimersRef.current[pumpId] = { off, tick };
          addToast('success', `เปิดปั๊มแล้ว ${duration} วินาที`);
        } else {
          setPumpLocalOn((p) => ({ ...p, [pumpId]: false }));
          setPumpCountdown((p) => ({ ...p, [pumpId]: 0 }));
        }
      }
    } catch (e) {
      addToast('error', e instanceof Error ? e.message : 'สั่งงานปั๊มไม่สำเร็จ');
    } finally {
      setPumpSending((p) => ({ ...p, [pumpId]: false }));
      telemetry.refetch();
    }
  };

  // ---- เช็คอัปเดต Firmware (OTA) ----
  // ต่างจากบอร์ด Tomato ตรง RPC method: บอร์ดนี้ใช้ "checkForUpdate" ไม่ใช่ "checkOta"
  // (ยึดจากโค้ดจริงใน ThingsBoardClient.cpp - เอกสารเก่าไม่ได้พูดถึง RPC นี้เลย
  // เพราะเพิ่งเพิ่มเข้ามาทีหลัง) ไม่ส่ง timeout = ยิงแบบ one-way ไม่ต้องรอบอร์ดเช็คเสร็จ
  // เหมือนปุ่ม OTA ของ Tomato แล้วปล่อยให้ telemetry (firmware_version/ota_status)
  // ที่ poll อยู่แล้วอัปเดตสถานะให้เองตามจริง
  const [otaState, setOtaState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const handleCheckOta = async () => {
    setOtaState('sending');
    try {
      await tbApi.sendRpc(project, gh, 'checkForUpdate', {});
      setOtaState('sent');
    } catch (e) {
      console.error(e);
      setOtaState('error');
    }
  };

  // ---- ปุ่มความปลอดภัย/รีเซ็ต ----
  const [clearingTrip, setClearingTrip] = useState(false);
  const handleClearSafetyTrip = async () => {
    setClearingTrip(true);
    try {
      await tbApi.sendRpc(project, gh, 'clearSafetyTrip', {}, 5000);
      addToast('success', 'ล้างสถานะ Safety Trip แล้ว');
    } catch (e) {
      addToast('error', e instanceof Error ? e.message : 'ล้าง Safety Trip ไม่สำเร็จ');
    } finally {
      setClearingTrip(false);
      telemetry.refetch();
    }
  };

  const [resettingCounters, setResettingCounters] = useState(false);
  const handleResetCounters = async () => {
    if (!window.confirm('รีเซ็ตตัวนับปริมาณโดสวันนี้ของทุกปั๊มเป็น 0?')) return;
    setResettingCounters(true);
    try {
      await tbApi.sendRpc(project, gh, 'resetDailyDoseCounters', {}, 5000);
      addToast('success', 'รีเซ็ตตัวนับโดสวันนี้แล้ว');
    } catch (e) {
      addToast('error', e instanceof Error ? e.message : 'รีเซ็ตไม่สำเร็จ');
    } finally {
      setResettingCounters(false);
      telemetry.refetch();
    }
  };

  const [activeTab, setActiveTab] = useState<AutoHydroTab>('overview');
  const [settingsGroup, setSettingsGroup] = useState<'ecph' | 'timing' | 'safety' | 'flow'>('ecph');

  if (!hasDevice) {
    return (
      <div className="relative">
        <BackgroundAurora />
        <GlassCard className="p-10 text-center">
          <Beaker className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
          <p className="font-medium text-gray-700 dark:text-gray-300">ยังไม่ได้ผูก ThingsBoard Device ID</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            ไปที่ Admin → โรงเรือน แล้วกรอก Device ID ของบอร์ด dosing controller ก่อน จึงจะเริ่มดึงข้อมูลได้
          </p>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="relative space-y-5 pb-20 md:pb-0 overflow-x-hidden">
      <BackgroundAurora />
      {/* Header */}
      <div className="rounded-3xl bg-gradient-to-br from-slate-900/90 via-indigo-950/90 to-slate-900/90 backdrop-blur-xl p-5 text-white shadow-[0_8px_32px_-8px_rgba(30,27,75,0.5)] border border-white/10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
              <Beaker className="w-[22px] h-[22px]" />
            </div>
            <div>
              <h1 className="font-bold text-lg leading-tight">{greenhouseName}</h1>
              <p className="text-xs text-white/50">ระบบผสมปุ๋ย/ปรับ pH อัตโนมัติ</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium border', attrs.isOnline ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-white/5 text-white/40 border-white/10')}>
              {attrs.isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {attrs.isOnline ? 'ออนไลน์' : 'ออฟไลน์'}
            </span>
            {mode && (
              <span className={cn('inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border', MODE_STYLE[mode] || MODE_STYLE.PAUSED)}>
                {mode}
              </span>
            )}
            {canControl && (
              <button
                onClick={handleCheckOta}
                disabled={otaState === 'sending'}
                className="rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-1 text-xs flex items-center gap-1.5 transition-colors disabled:opacity-50"
                title="สั่งให้บอร์ดเช็คอัปเดต firmware ระยะไกล"
              >
                <DownloadCloud className={cn('w-3 h-3', otaState === 'sending' && 'animate-pulse')} />
                {otaState === 'sent' ? 'ส่งคำสั่งแล้ว' : otaState === 'error' ? 'ส่งไม่สำเร็จ ลองใหม่' : 'เช็คอัปเดต Firmware'}
              </button>
            )}
            <button
              onClick={() => { telemetry.refetch(); attrs.refetch(); }}
              className="rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-1 text-xs flex items-center gap-1.5 transition-colors"
            >
              <RefreshCw className={cn('w-3 h-3', telemetry.isLoading && 'animate-spin')} /> รีเฟรช
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-xs text-white/50">
          {dosingState && <span>สถานะ: <span className="text-white/80">{DOSING_STATE_LABELS[dosingState] || dosingState}</span></span>}
          {fwVersion && <span>Firmware v{fwVersion}</span>}
          {wifiRssi !== null && <span>สัญญาณ WiFi: {wifiRssi} dBm</span>}
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> uptime {formatUptime(uptimeSec)}</span>
        </div>
        {otaState === 'sent' && (
          <p className="text-xs text-white/40 mt-1">
            ส่งคำสั่งไปยังบอร์ดแล้ว ถ้ามีเวอร์ชันใหม่บอร์ดจะดาวน์โหลดและรีสตาร์ทเอง{otaStatus ? ` (${otaStatus})` : ''} รีเฟรชหน้านี้ภายหลังเพื่อดู Firmware version ล่าสุด
          </p>
        )}
      </div>

      {/* Safety / alert banners */}
      {safetyTripped && (
        <div className="rounded-2xl border-2 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950 p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-6 h-6 text-red-600 dark:text-red-400 shrink-0" />
            <div>
              <p className="font-semibold text-red-800 dark:text-red-300 text-sm">Safety Trip — หยุดโดสทั้งหมด</p>
              <p className="text-xs text-red-600 dark:text-red-400">เซนเซอร์อ่านค่าผิดพลาดติดต่อกันจนระบบหยุดทำงานเพื่อความปลอดภัย ตรวจเซนเซอร์/สายไฟก่อนล้างสถานะ</p>
            </div>
          </div>
          {canControl && (
            <Button variant="danger" size="sm" onClick={handleClearSafetyTrip} isLoading={clearingTrip}>
              ล้าง Safety Trip
            </Button>
          )}
        </div>
      )}
      {phBelowMinAlert && !safetyTripped && (
        <div className="rounded-2xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
          <div>
            <p className="font-semibold text-amber-800 dark:text-amber-300 text-sm">pH ต่ำกว่าเป้าล่าง</p>
            <p className="text-xs text-amber-600 dark:text-amber-400">ระบบไม่มีปั๊ม pH Up จึงแก้อัตโนมัติไม่ได้ — ต้องปรับ pH ขึ้นด้วยมือ</p>
          </div>
        </div>
      )}

      {/* Tab bar — glass pill segmented control (desktop only; mobile gets a
          fixed bottom bar below, matching the rest of the app's convention
          of GreenhouseTabs on desktop / GreenhouseTabsBottom on mobile) */}
      <nav className="hidden md:flex gap-1 overflow-x-auto rounded-2xl border border-white/60 dark:border-white/10 bg-white/45 dark:bg-white/[0.05] backdrop-blur-xl p-1.5 shadow-[0_8px_32px_-12px_rgba(31,38,135,0.15)]">
        {PAGE_TABS.map((tabDef) => {
          const Icon = tabDef.icon;
          const isActive = activeTab === tabDef.id;
          return (
            <button
              key={tabDef.id}
              onClick={() => setActiveTab(tabDef.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all',
                isActive
                  ? 'bg-white/90 dark:bg-white/15 text-indigo-700 dark:text-indigo-300 shadow-md'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-white/40 dark:hover:bg-white/10'
              )}
            >
              <Icon className="w-4 h-4" />
              {tabDef.label}
            </button>
          );
        })}
      </nav>

      {activeTab === 'overview' && (
        <div className="grid gap-5 lg:grid-cols-3 items-start">
          <GlassCard className="p-5 lg:col-span-2">
            <SectionTitle icon={Activity} iconBg="#E6F1FB" iconColor="#185FA5" title="ค่าเซนเซอร์" />
            <div className="grid grid-cols-2 gap-3">
              <SensorStat
                icon={Beaker} label="EC" value={ec} unit="µS/cm" colorRgb="216,90,48" decimals={0}
                warnMin={(attrs.data.ec_target_min as number | null) ?? 1000} warnMax={(attrs.data.ec_target_max as number | null) ?? 3500}
              />
              <SensorStat
                icon={FlaskConical} label="pH" value={ph} unit="" colorRgb="24,95,165" decimals={2}
                warnMin={(attrs.data.ph_target_min as number | null) ?? 5.5} warnMax={(attrs.data.ph_target_max as number | null) ?? 6.5}
              />
              <SensorStat icon={Thermometer} label="อุณหภูมิน้ำ" value={waterTemp} unit="°C" colorRgb="15,110,86" warnMin={18} warnMax={28} />
              <SensorStat icon={Thermometer} label="อุณหภูมิอากาศ" value={airTemp} unit="°C" colorRgb="153,60,29" warnMin={18} warnMax={32} />
              <SensorStat icon={Wind} label="ความชื้นอากาศ" value={airHumidity} unit="%" colorRgb="15,110,86" warnMin={40} warnMax={80} />
              <SensorStat icon={Sun} label="แสง" value={airLight} unit="lux" colorRgb="133,79,11" decimals={0} />
            </div>
          </GlassCard>

          <GlassCard className="p-5">
            <SectionTitle icon={Droplets} iconBg="#EAF3DE" iconColor="#3B6D11" title="สถานะระบบ" />
            <div className="space-y-2">
              {PUMPS.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-2.5 rounded-xl border border-white/50 dark:border-white/10 px-3 py-2"
                  style={{ background: pumpOn[p.id] ? `rgba(${p.colorRgb},0.10)` : undefined }}
                >
                  <span className="w-1.5 h-6 rounded-full shrink-0" style={{ background: `rgb(${p.colorRgb})` }} />
                  <span className="font-medium text-sm text-gray-800 dark:text-gray-200 flex-1">{p.name}</span>
                  <span className={cn(
                    'inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full',
                    pumpOn[p.id] ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/10' : 'text-gray-400 dark:text-gray-500'
                  )}>
                    <span className={cn('w-1.5 h-1.5 rounded-full', pumpOn[p.id] ? p.dot : 'bg-gray-300 dark:bg-gray-600')} />
                    {pumpOn[p.id] ? 'กำลังทำงาน' : 'หยุด'}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-5 mb-2">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">ปริมาณโดสวันนี้</span>
              {canControl && (
                <button
                  onClick={handleResetCounters}
                  disabled={resettingCounters}
                  className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex items-center gap-1 disabled:opacity-50"
                >
                  <RotateCcw className={cn('w-3 h-3', resettingCounters && 'animate-spin')} /> รีเซ็ต
                </button>
              )}
            </div>
            <div className="space-y-3">
              <DoseBar label="Nutrient A" value={t.total_dose_ml_today_a as number | null ?? null} max={attrs.data.max_dose_ml_per_day_a as number | null ?? null} colorClass="bg-emerald-500" />
              <DoseBar label="Nutrient B" value={t.total_dose_ml_today_b as number | null ?? null} max={attrs.data.max_dose_ml_per_day_b as number | null ?? null} colorClass="bg-sky-500" />
              <DoseBar label="pH Down" value={t.total_dose_ml_today_ph as number | null ?? null} max={attrs.data.max_dose_ml_per_day_ph as number | null ?? null} colorClass="bg-amber-500" />
            </div>
          </GlassCard>
        </div>
      )}

      {activeTab === 'chart' && (
        <HistoryChartSection project={project} gh={gh} greenhouseId={greenhouseId} />
      )}

      {activeTab === 'control' && (
        <>
          <GlassCard className="p-5">
            <SectionTitle icon={Zap} iconBg="#EEF2FF" iconColor="#4338CA" title="โหมดการทำงาน" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {(['AUTO', 'MANUAL', 'PAUSED'] as const).map((m) => {
                const meta = MODE_META[m];
                const MetaIcon = meta.icon;
                const isActive = mode === m;
                return (
                  <button
                    key={m}
                    disabled={!canControl || modeSending}
                    onClick={() => handleSetMode(m)}
                    className={cn(
                      'relative rounded-2xl border-2 backdrop-blur-md p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed',
                      isActive
                        ? 'shadow-lg'
                        : 'border-white/50 dark:border-white/10 bg-white/25 dark:bg-white/[0.03] hover:bg-white/45 dark:hover:bg-white/10 hover:shadow-md'
                    )}
                    style={isActive ? {
                      borderColor: `rgb(${meta.colorRgb})`,
                      background: `linear-gradient(135deg, rgba(${meta.colorRgb},0.22), rgba(${meta.colorRgb},0.06))`,
                    } : undefined}
                  >
                    {isActive && (
                      <span className="absolute top-3 right-3 w-2 h-2 rounded-full" style={{ background: `rgb(${meta.colorRgb})` }} />
                    )}
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                      style={isActive
                        ? { background: `rgb(${meta.colorRgb})`, color: 'white' }
                        : { background: `rgba(${meta.colorRgb},0.1)`, color: `rgb(${meta.colorRgb})` }}
                    >
                      <MetaIcon className="w-5 h-5" />
                    </div>
                    <div
                      className={cn('font-bold text-sm mb-0.5', !isActive && 'text-gray-700 dark:text-gray-300')}
                      style={isActive ? { color: `rgb(${meta.colorRgb})` } : undefined}
                    >
                      {m}
                    </div>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 leading-snug">{meta.caption}</p>
                  </button>
                );
              })}
            </div>
            {!canControl && <p className="text-xs text-gray-400 mt-2">ต้องมีสิทธิ์ operator ขึ้นไปจึงจะเปลี่ยนโหมดได้</p>}
          </GlassCard>

          <GlassCard className="p-5">
            <SectionTitle icon={Power} iconBg="#ECFDF5" iconColor="#047857" title="ควบคุมปั๊มด้วยตนเอง" />
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-3 -mt-2.5">
              ควบคุมได้เฉพาะตอนโหมด MANUAL เท่านั้น {mode !== 'MANUAL' && '(ตอนนี้ปิดใช้งานอยู่)'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {PUMPS.map((p) => {
                const on = pumpLocalOn[p.id] !== undefined ? pumpLocalOn[p.id]! : pumpOn[p.id];
                const countdown = pumpCountdown[p.id] ?? 0;
                const canOperate = canControl && mode === 'MANUAL' && !pumpSending[p.id];
                return (
                  <div
                    key={p.id}
                    className={cn(
                      'overflow-hidden rounded-2xl border border-white/60 dark:border-white/10 bg-white/70 dark:bg-white/5 backdrop-blur-md transition-all',
                      on && 'shadow-lg'
                    )}
                    style={on ? { boxShadow: `0 0 0 2px rgb(${p.colorRgb}), 0 10px 25px -10px rgba(${p.colorRgb},0.4)` } : undefined}
                  >
                    {/* status bar */}
                    <div className="h-2 transition-all duration-500" style={{ background: on ? `rgb(${p.colorRgb})` : '#e5e7eb' }} />

                    <div className="p-5 flex flex-col items-center text-center">
                      <h3 className="font-bold text-base mb-1 text-gray-900 dark:text-gray-100">{p.name}</h3>
                      <div className={cn(
                        'inline-flex items-center gap-1.5 text-xs font-semibold mb-4',
                        on ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500'
                      )}>
                        <span className={cn('w-1.5 h-1.5 rounded-full', on ? 'bg-emerald-500 animate-pulse' : 'bg-gray-300 dark:bg-gray-600')} />
                        {on ? 'กำลังทำงาน' : 'หยุด'}
                      </div>

                      {/* Big press button - the primary control */}
                      <button
                        onClick={() => handlePump(p.id, true)}
                        disabled={!canOperate}
                        className="relative w-24 h-24 rounded-full flex items-center justify-center transition-all duration-300 active:scale-90 disabled:opacity-40 disabled:cursor-not-allowed mb-2"
                        style={on
                          ? { background: `linear-gradient(145deg, rgb(${p.colorRgb}), rgba(${p.colorRgb},0.75))`, boxShadow: `0 8px 24px -6px rgba(${p.colorRgb},0.6)` }
                          : { background: '#f3f4f6' }}
                      >
                        {on && (
                          <span
                            className="absolute inset-0 rounded-full animate-ping"
                            style={{ background: `rgb(${p.colorRgb})`, opacity: 0.3 }}
                          />
                        )}
                        {on && countdown > 0 ? (
                          <span className="relative text-4xl font-extrabold text-white tabular-nums leading-none">
                            {countdown}
                          </span>
                        ) : (
                          <Droplets className="relative w-10 h-10" style={{ color: on ? 'white' : '#9ca3af' }} />
                        )}
                      </button>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
                        {pumpSending[p.id]
                          ? 'กำลังส่ง...'
                          : on && countdown > 0
                            ? `เหลืออีก ${countdown} วินาที`
                            : 'แตะเพื่อเปิด'}
                      </p>

                      {/* Duration stepper */}
                      <div className="flex items-center gap-3 mb-4">
                        <button
                          onClick={() => setPumpDuration((d) => ({ ...d, [p.id]: String(Math.max(1, (Number(d[p.id]) || 1) - 1)) }))}
                          disabled={!canControl || mode !== 'MANUAL'}
                          className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-300 disabled:opacity-40 active:scale-90 transition-all"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <div className="w-16 text-center">
                          <span className="text-lg font-bold tabular-nums text-gray-800 dark:text-gray-200">{pumpDuration[p.id]}</span>
                          <span className="text-xs text-gray-400 ml-1">วิ</span>
                        </div>
                        <button
                          onClick={() => setPumpDuration((d) => ({ ...d, [p.id]: String(Math.min(30, (Number(d[p.id]) || 1) + 1)) }))}
                          disabled={!canControl || mode !== 'MANUAL'}
                          className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-300 disabled:opacity-40 active:scale-90 transition-all"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      <button
                        onClick={() => handlePump(p.id, false)}
                        disabled={!canOperate}
                        className="text-xs font-medium text-red-500 dark:text-red-400 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                      >
                        <StopCircle className="w-3.5 h-3.5" /> หยุดทันที
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </GlassCard>
        </>
      )}

      {activeTab === 'settings' && (
        <div className="grid gap-5 lg:grid-cols-[220px_1fr] items-start">
          {/* group nav — vertical on desktop, horizontal pills on mobile.
              min-w-0 is load-bearing here: without it, a flex/grid item with
              an overflow-x-auto descendant defaults to min-width:auto and
              refuses to shrink below its unwrapped content's width, which
              blows out this grid column - and with it the whole page's
              width - instead of scrolling locally like it's supposed to. */}
          <GlassCard className="p-2.5 lg:sticky lg:top-5 min-w-0">
            <div className="flex lg:flex-col gap-1.5 overflow-x-auto min-w-0">
              {SETTINGS_GROUPS.map((g) => {
                const GIcon = g.icon;
                const isActive = settingsGroup === g.id;
                return (
                  <button
                    key={g.id}
                    onClick={() => setSettingsGroup(g.id)}
                    className={cn(
                      'flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap text-left transition-colors',
                      isActive ? 'bg-white/80 dark:bg-white/15 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:bg-white/40 dark:hover:bg-white/10'
                    )}
                  >
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: g.iconBg, color: g.iconColor }}
                    >
                      <GIcon className="w-3.5 h-3.5" />
                    </div>
                    <span className={isActive ? 'text-gray-900 dark:text-gray-100' : undefined}>{g.label}</span>
                  </button>
                );
              })}
            </div>
          </GlassCard>

          <GlassCard className="p-5 min-w-0">
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">การเปลี่ยนแปลงจะส่งตรงไปที่บอร์ดทันทีเมื่อกดบันทึก</p>
            {settingsGroup === 'ecph' && (
              <div className="grid sm:grid-cols-2 gap-3">
                {EC_PH_FIELDS.map((f) => (
                  <Input
                    key={f.key} label={`${f.label}${f.unit ? ` (${f.unit})` : ''}`} type="number" step={f.step} min={f.min} max={f.max}
                    value={draft[f.key] ?? ''} disabled={!canControl}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  />
                ))}
              </div>
            )}

            {settingsGroup === 'timing' && (
              <div className="grid sm:grid-cols-2 gap-3">
                {TIMING_FIELDS.map((f) => (
                  <Input
                    key={f.key} label={`${f.label} (${f.unit})`} type="number" step={f.step} min={f.min} max={f.max}
                    value={draft[f.key] ?? ''} disabled={!canControl}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  />
                ))}
              </div>
            )}

            {settingsGroup === 'safety' && (
              <div className="grid sm:grid-cols-2 gap-3">
                {SAFETY_FIELDS.map((f) => (
                  <Input
                    key={f.key} label={`${f.label} (${f.unit})`} type="number" step={f.step} min={f.min} max={f.max}
                    value={draft[f.key] ?? ''} disabled={!canControl}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  />
                ))}
              </div>
            )}

            {settingsGroup === 'flow' && (
              <div>
                <div className="grid sm:grid-cols-3 gap-3">
                  {FLOW_RATE_FIELDS.map((f) => (
                    <div key={f.key} className="rounded-xl border border-white/50 dark:border-white/10 bg-white/25 dark:bg-white/[0.03] backdrop-blur-md px-3 py-2">
                      <div className="text-xs text-gray-500 dark:text-gray-400">{f.label}</div>
                      <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                        {attrs.data[f.key] !== undefined ? `${formatNumber(Number(attrs.data[f.key]), 2)} ml/วิ` : '--'}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                  ค่านี้ต้อง calibrate จริงต่อปั๊ม (ดูขั้นตอนใน README ของเฟิร์มแวร์) ไม่แก้ตรงนี้เพื่อกันพลาดปริมาณโดสจริง
                </p>
              </div>
            )}

            {canControl && settingsGroup !== 'flow' && (
              <div className="mt-5 flex justify-end">
                <Button onClick={handleSaveSettings} isLoading={savingSettings} className="shadow-lg">บันทึกการตั้งค่า</Button>
              </div>
            )}
          </GlassCard>
        </div>
      )}

      {/* Bottom tab bar — mobile only, same pattern as GreenhouseTabsBottom.tsx
          used by every other project page (fixed, icon + label, active = primary) */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 md:hidden z-50 safe-area-bottom">
        <nav className="flex justify-around">
          {PAGE_TABS.map((tabDef) => {
            const Icon = tabDef.icon;
            const isActive = activeTab === tabDef.id;
            return (
              <button
                key={tabDef.id}
                onClick={() => setActiveTab(tabDef.id)}
                className={cn(
                  'flex flex-col items-center justify-center py-2 px-3 flex-1 transition-colors active:bg-gray-100 dark:active:bg-gray-800',
                  isActive ? 'text-primary' : 'text-gray-500 dark:text-gray-400'
                )}
              >
                <Icon className={cn('h-6 w-6 mb-1', isActive ? 'text-primary' : 'text-gray-400 dark:text-gray-500')} />
                <span className="text-xs font-medium">{tabDef.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
